import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet, StatusActionButton, WorkTypeSelector } from '../../../shared/ui';
import {
  contractsV2Api,
  CreateContractRequestV2,
  CreateContractEstimateItemV2,
  ContractParticipantRequestV2
} from '../../../api/contractsV2Api';
import { estimatesApi, Estimate, EstimateItem, EstimateItemWithUsage } from '../../../api/estimatesApi';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import './ContractsCard.css';

interface CreateContractModalProps {
  isOpen: boolean;
  siteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

type CreateMode = 'manual' | 'estimate';

interface SelectedItemRow {
  item: EstimateItemWithUsage;
  contractQuantity: string;
  deductionPercent: string;
}

interface SelectedParticipant {
  executor: SiteExecutor;
  sharePercent: string;
}

const CreateContractModal: React.FC<CreateContractModalProps> = ({
  isOpen,
  siteId,
  onClose,
  onSuccess
}) => {
  const [mode, setMode] = useState<CreateMode>('manual');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [typeOfWorkId, setTypeOfWorkId] = useState<number | null>(null);
  const [fixedAmount, setFixedAmount] = useState<string>('');
  const [deductionPercent, setDeductionPercent] = useState<string>('0');

  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null);
  const [estimateItems, setEstimateItems] = useState<EstimateItemWithUsage[]>([]);
  const [selectedTypeOfWorkId, setSelectedTypeOfWorkId] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<SelectedItemRow[]>([]);
  const [executors, setExecutors] = useState<SiteExecutor[]>([]);
  const [participants, setParticipants] = useState<SelectedParticipant[]>([]);

  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  const loadEstimates = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await estimatesApi.getSiteEstimates(siteId);
      setEstimates(data);
      if (data.length === 1) setSelectedEstimateId(data[0].id);
    } catch {
      setEstimates([]);
    }
  }, [siteId]);

  const loadEstimateItems = useCallback(async () => {
    if (!siteId || !selectedEstimateId) return;
    setItemsLoading(true);
    try {
      const items = await estimatesApi.getEstimateItemsForContract(siteId, selectedEstimateId);
      const withType = items.filter((i) => i.typeOfWorkId && !i.isTotalRow);
      setEstimateItems(withType);
      const typeIds = Array.from(new Set(withType.map((i) => i.typeOfWorkId!)));
      if (typeIds.length === 1) setSelectedTypeOfWorkId(typeIds[0]);
      else if (typeIds.length > 1) setSelectedTypeOfWorkId(null);
      setSelectedRows([]);
    } catch {
      setEstimateItems([]);
    } finally {
      setItemsLoading(false);
    }
  }, [siteId, selectedEstimateId]);

  const loadExecutors = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await siteExecutorsApi.getSiteExecutors(siteId);
      setExecutors(data);
    } catch {
      setExecutors([]);
    }
  }, [siteId]);

  useEffect(() => {
    if (isOpen && mode === 'estimate') {
      loadEstimates();
    }
  }, [isOpen, mode, loadEstimates]);

  useEffect(() => {
    if (isOpen) loadExecutors();
  }, [isOpen, loadExecutors]);

  useEffect(() => {
    if (selectedEstimateId && mode === 'estimate') {
      loadEstimateItems();
    }
  }, [selectedEstimateId, mode, loadEstimateItems]);

  const itemsByType = estimateItems.filter((i) => i.typeOfWorkId === selectedTypeOfWorkId);

  const addItemToSelection = (item: EstimateItemWithUsage) => {
    if (selectedRows.some((r) => r.item.id === item.id)) return;
    const qty = (item.quantity ?? 0).toString();
    setSelectedRows((prev) => [...prev, { item, contractQuantity: qty, deductionPercent: '0' }]);
  };

  const removeItemFromSelection = (itemId: number) => {
    setSelectedRows((prev) => prev.filter((r) => r.item.id !== itemId));
  };

  const updateRow = (itemId: number, field: 'contractQuantity' | 'deductionPercent', value: string) => {
    setSelectedRows((prev) =>
      prev.map((r) => (r.item.id === itemId ? { ...r, [field]: value } : r))
    );
  };

  const formatAmount = (n: number) => new Intl.NumberFormat('ru-RU').format(Math.round(n)) + ' ₽';

  const getRowGross = (row: SelectedItemRow) => {
    const qty = parseFloat(row.contractQuantity.replace(',', '.')) || 0;
    const price = row.item.pricePerUnit ?? 0;
    return qty * price;
  };

  const getRowNet = (row: SelectedItemRow) => {
    const gross = getRowGross(row);
    const pct = parseFloat(row.deductionPercent.replace(',', '.')) || 0;
    return gross * (1 - pct / 100);
  };

  const totalNetAmount = selectedRows.reduce((sum, r) => sum + getRowNet(r), 0);

  const addParticipant = (executor: SiteExecutor) => {
    if (participants.some((p) => p.executor.id === executor.id)) return;
    setParticipants((prev) => [...prev, { executor, sharePercent: '0' }]);
  };

  const removeParticipant = (executorId: number) => {
    setParticipants((prev) => prev.filter((p) => p.executor.id !== executorId));
  };

  const updateParticipantShare = (executorId: number, value: string) => {
    setParticipants((prev) =>
      prev.map((p) => (p.executor.id === executorId ? { ...p, sharePercent: value } : p))
    );
  };

  const totalSharePercent = participants.reduce((sum, p) => {
    const v = parseFloat(p.sharePercent.replace(',', '.')) || 0;
    return sum + v;
  }, 0);

  const participantsValid = participants.length === 0 || (totalSharePercent > 0 && totalSharePercent <= 100.01);

  const getParticipantsPayload = (): ContractParticipantRequestV2[] | undefined => {
    if (participants.length === 0) return undefined;
    return participants.map((p) => ({
      siteExecutorId: p.executor.id,
      sharePercent: parseFloat(p.sharePercent.replace(',', '.')) || 0
    }));
  };

  const canProceedToItems =
    mode === 'estimate' &&
    selectedEstimateId &&
    estimateItems.length > 0 &&
    selectedTypeOfWorkId;

  const canProceedToSubmit =
    mode === 'estimate' &&
    selectedRows.length > 0 &&
    selectedRows.every((r) => {
      const qty = parseFloat(r.contractQuantity.replace(',', '.')) || 0;
      const maxQty = r.item.quantity ?? 0;
      const pct = parseFloat(r.deductionPercent.replace(',', '.')) ?? 0;
      return qty > 0 && qty <= maxQty && pct >= 0 && pct <= 100;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Введите название контракта');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'manual') {
        if (!typeOfWorkId) {
          setError('Выберите вид работы');
          setLoading(false);
          return;
        }
        const amount = parseFloat(fixedAmount.replace(',', '.'));
        if (isNaN(amount) || amount <= 0) {
          setError('Введите корректную сумму');
          setLoading(false);
          return;
        }
        const percent = parseFloat(deductionPercent.replace(',', '.')) || 0;
        if (isNaN(percent) || percent < 0 || percent > 100) {
          setError('Процент вычета должен быть от 0 до 100');
          setLoading(false);
          return;
        }
        const data: CreateContractRequestV2 = {
          title: title.trim(),
          description: description.trim() || undefined,
          typeOfWorkId,
          fixedAmount: amount,
          deductionPercent: percent,
          participants: getParticipantsPayload()
        };
        await contractsV2Api.createContract(siteId, data);
      } else {
        const items: CreateContractEstimateItemV2[] = selectedRows.map((r) => ({
          estimateItemId: r.item.id,
          contractQuantity: parseFloat(r.contractQuantity.replace(',', '.')) || 0,
          deductionPercent: parseFloat(r.deductionPercent.replace(',', '.')) || 0
        }));
        const data: CreateContractRequestV2 = {
          title: title.trim(),
          description: description.trim() || undefined,
          estimateId: selectedEstimateId ?? undefined,
          items,
          participants: getParticipantsPayload()
        };
        await contractsV2Api.createContract(siteId, data);
      }

      onSuccess();
      resetAndClose();
    } catch (err: unknown) {
      console.error('Error creating contract:', err);
      const res = err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: unknown; status?: number } }).response : null;
      let msg: string | null = null;
      if (res?.data) {
        const d = res.data;
        if (typeof d === 'string') msg = d;
        else if (d && typeof d === 'object') {
          const obj = d as Record<string, unknown>;
          msg = [obj.message, obj.detail, obj.title].find((v) => typeof v === 'string') as string | undefined ?? null;
        }
      }
      setError(msg || 'Не удалось создать контракт');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setMode('manual');
    setTitle('');
    setDescription('');
    setTypeOfWorkId(null);
    setFixedAmount('');
    setDeductionPercent('0');
    setSelectedEstimateId(null);
    setEstimateItems([]);
    setSelectedTypeOfWorkId(null);
    setSelectedRows([]);
    setParticipants([]);
    setError(null);
    onClose();
  };

  const handleClose = () => resetAndClose();

  const renderStep1 = () => (
    <>
      <div className="form-group">
        <label>Название *</label>
        <input
          type="text"
          placeholder="Например: Плитка кухня"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
      </div>
      <div className="form-group">
        <label>Источник</label>
        <div className="create-contract-mode-tabs">
          <button
            type="button"
            className={`mode-tab ${mode === 'manual' ? 'active' : ''}`}
            onClick={() => setMode('manual')}
          >
            Вручную
          </button>
          <button
            type="button"
            className={`mode-tab ${mode === 'estimate' ? 'active' : ''}`}
            onClick={() => setMode('estimate')}
          >
            Из сметы
          </button>
        </div>
      </div>

      {mode === 'manual' && (
        <>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              placeholder="Дополнительная информация (необязательно)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="form-group">
            <WorkTypeSelector
              label="Вид работы *"
              value={typeOfWorkId}
              onChange={setTypeOfWorkId}
              required
              placeholder="Выберите вид работы"
            />
          </div>
          <div className="form-group">
            <label>Сумма контракта (₽) *</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={fixedAmount}
              onChange={(e) => setFixedAmount(e.target.value.replace(/[^\d.,]/g, ''))}
            />
          </div>
          <div className="form-group">
            <label>Процент вычета (%)</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={deductionPercent}
              onChange={(e) => setDeductionPercent(e.target.value.replace(/[^\d.,]/g, '') || '0')}
            />
            <span className="form-hint">Остаётся в смете (для руководителя объекта)</span>
          </div>
          <div className="form-group contract-participants-section">
            <label>Участники контракта</label>
            <span className="form-hint">Добавьте исполнителей и укажите долю каждого (сумма до 100%)</span>
            <select
              className="contract-participant-add"
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (id) addParticipant(executors.find((ex) => ex.id === id)!);
                e.target.value = '';
              }}
            >
              <option value="">+ Добавить участника</option>
              {executors
                .filter((ex) => !participants.some((p) => p.executor.id === ex.id))
                .map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.displayName} {ex.isExternal ? '(внешний)' : ''}
                  </option>
                ))}
            </select>
            {participants.length > 0 && (
              <div className="contract-participants-list">
                {participants.map((p) => (
                  <div key={p.executor.id} className="contract-participant-row">
                    <span className="participant-name">{p.executor.displayName}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="%"
                      value={p.sharePercent}
                      onChange={(e) =>
                        updateParticipantShare(p.executor.id, e.target.value.replace(/[^\d.,]/g, ''))
                      }
                      className="participant-share-input"
                    />
                    <button
                      type="button"
                      className="participant-remove"
                      onClick={() => removeParticipant(p.executor.id)}
                      title="Удалить"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
                <div className="contract-participants-total">
                  Итого: {totalSharePercent.toFixed(1)}%
                  {totalSharePercent > 100 && (
                    <span className="participants-error"> — превышает 100%</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="create-contract-actions">
            <StatusActionButton variant="secondary" onClick={handleClose} disabled={loading}>
              Отмена
            </StatusActionButton>
            <StatusActionButton
              variant="primary"
              type="button"
              disabled={loading || !title.trim() || !typeOfWorkId || !fixedAmount || !participantsValid}
              onClick={() => formRef.current?.requestSubmit()}
            >
              {loading ? 'Создание...' : 'Создать'}
            </StatusActionButton>
          </div>
        </>
      )}

      {mode === 'estimate' && (
        <>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              placeholder="Дополнительная информация (необязательно)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Смета *</label>
            <select
              value={selectedEstimateId ?? ''}
              onChange={(e) => setSelectedEstimateId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Выберите смету</option>
              {estimates.map((est) => (
                <option key={est.id} value={est.id}>
                  {est.name}
                </option>
              ))}
            </select>
          </div>
          {itemsLoading && <p className="form-hint">Загрузка пунктов...</p>}
          {!itemsLoading && estimateItems.length === 0 && selectedEstimateId && (
            <p className="create-contract-warning">В смете нет пунктов с указанным видом работы</p>
          )}
          {!itemsLoading && estimateItems.length > 0 && (
            <>
              <div className="form-group">
                <label>Вид работы *</label>
                <select
                  value={selectedTypeOfWorkId ?? ''}
                  onChange={(e) =>
                    setSelectedTypeOfWorkId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">Выберите вид работы</option>
                  {Array.from(new Set(estimateItems.map((i) => i.typeOfWorkId!))).map((tid) => {
                    const item = estimateItems.find((i) => i.typeOfWorkId === tid)!;
                    return (
                      <option key={tid} value={tid}>
                        {item.typeOfWork?.name ?? `ID ${tid}`}
                      </option>
                    );
                  })}
                </select>
              </div>
              {selectedTypeOfWorkId && itemsByType.length > 0 && (
                <div className="form-group">
                  <label>Пункты сметы — укажите объём и вычет по каждому</label>
                  <span className="form-hint">Показаны остатки по смете и суммы в контрактах. Перерасход возможен, но будет отмечен.</span>
                  <div className="estimate-items-select">
                    {itemsByType.map((item) => {
                      const row = selectedRows.find((r) => r.item.id === item.id);
                      const isSelected = !!row;
                      const hasContractUsage = (item.contractedQuantity ?? 0) > 0;
                      const remainingQty = item.remainingQuantity ?? item.quantity ?? 0;
                      const contractQty = parseFloat(row?.contractQuantity.replace(',', '.') || '0') || 0;
                      const isOverExpenditure = isSelected && contractQty > remainingQty;
                      return (
                        <div key={item.id} className={`estimate-item-select-row ${isOverExpenditure ? 'estimate-item-over-expenditure' : ''}`}>
                          <label className="estimate-item-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                isSelected
                                  ? removeItemFromSelection(item.id)
                                  : addItemToSelection(item)
                              }
                            />
                            <span className="item-label">
                              {item.name} — {item.quantity} {item.unit ?? 'шт.'} × {formatAmount(item.pricePerUnit ?? 0)}
                              {hasContractUsage && (
                                <span className="item-contract-usage">
                                  {' '}(в контрактах: {item.contractedQuantity} {item.unit ?? 'шт.'}, {formatAmount(item.contractedNetAmount ?? 0)}; остаток: {remainingQty} {item.unit ?? 'шт.'})
                                </span>
                              )}
                            </span>
                          </label>
                          {isSelected && row && (
                            <div className="item-inline-fields">
                              <div className="field">
                                <label>Объём</label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder={remainingQty > 0 ? `остаток ${remainingQty}` : 'можно превысить'}
                                  value={row.contractQuantity}
                                  onChange={(e) =>
                                    updateRow(
                                      item.id,
                                      'contractQuantity',
                                      e.target.value.replace(/[^\d.,]/g, '')
                                    )
                                  }
                                />
                              </div>
                              <div className="field">
                                <label>Вычет %</label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="0"
                                  value={row.deductionPercent}
                                  onChange={(e) =>
                                    updateRow(
                                      item.id,
                                      'deductionPercent',
                                      e.target.value.replace(/[^\d.,]/g, '') || '0'
                                    )
                                  }
                                />
                              </div>
                              <div className="field-calc">
                                {formatAmount(getRowGross(row))} → {formatAmount(getRowNet(row))}
                                {isOverExpenditure && (
                                  <span className="over-expenditure-warning" title="Объём превышает остаток по смете">
                                    <i className="ri-error-warning-line"></i> Перерасход
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedRows.length > 0 && (
                    <div className="create-contract-total create-contract-total-inline">
                      <strong>Итого к выплате:</strong> {formatAmount(totalNetAmount)}
                    </div>
                  )}
                </div>
              )}
              <div className="form-group contract-participants-section">
                <label>Участники контракта</label>
                <span className="form-hint">Добавьте исполнителей и укажите долю каждого (сумма до 100%)</span>
                <select
                  className="contract-participant-add"
                  value=""
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (id) addParticipant(executors.find((ex) => ex.id === id)!);
                    e.target.value = '';
                  }}
                >
                  <option value="">+ Добавить участника</option>
                  {executors
                    .filter((ex) => !participants.some((p) => p.executor.id === ex.id))
                    .map((ex) => (
                      <option key={ex.id} value={ex.id}>
                        {ex.displayName} {ex.isExternal ? '(внешний)' : ''}
                      </option>
                    ))}
                </select>
                {participants.length > 0 && (
                  <div className="contract-participants-list">
                    {participants.map((p) => (
                      <div key={p.executor.id} className="contract-participant-row">
                        <span className="participant-name">{p.executor.displayName}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="%"
                          value={p.sharePercent}
                          onChange={(e) =>
                            updateParticipantShare(p.executor.id, e.target.value.replace(/[^\d.,]/g, ''))
                          }
                          className="participant-share-input"
                        />
                        <button
                          type="button"
                          className="participant-remove"
                          onClick={() => removeParticipant(p.executor.id)}
                          title="Удалить"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ))}
                    <div className="contract-participants-total">
                      Итого: {totalSharePercent.toFixed(1)}%
                      {totalSharePercent > 100 && (
                        <span className="participants-error"> — превышает 100%</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="create-contract-actions">
                <StatusActionButton variant="secondary" onClick={handleClose} disabled={loading}>
                  Отмена
                </StatusActionButton>
                <StatusActionButton
                  variant="primary"
                  type="button"
                  disabled={loading || !canProceedToItems || !canProceedToSubmit || !participantsValid}
                  onClick={() => formRef.current?.requestSubmit()}
                >
                  {loading ? 'Создание...' : 'Создать контракт'}
                </StatusActionButton>
              </div>
            </>
          )}
          {mode === 'estimate' && !canProceedToItems && (
            <div className="create-contract-actions">
              <StatusActionButton variant="secondary" onClick={handleClose} disabled={loading}>
                Отмена
              </StatusActionButton>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Создать контракт"
      size="medium"
      showHandle={true}
    >
      <form ref={formRef} onSubmit={handleSubmit} className="create-contract-form">
        {error && (
          <div className="create-contract-error">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}
        {renderStep1()}
      </form>
    </BottomSheet>
  );
};

export default CreateContractModal;
